import { updatePaymentMethod } from "@/api/rest/checkoutApi";
import { MobileStripePaymentForm } from "@/components/Checkout/components/CheckoutV2/CheckoutForm/ExperimentalMobile/components/payment/MobileStripePaymentForm";
import { BrandclubStoreConfigUtils, hexadecimal } from "@brandclub/common-ui";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCheckoutUI } from "../../../../CheckoutUIProvider";
import { CheckoutIntent } from "../../../../types/CheckoutIntent";
import { useState } from "react";
import { Skeleton, useTheme } from "@mui/material";
import { useAppSelector } from "@/redux/hooks";

import useStoreType from "@/utils/hooks/useStoreType";
import { StoreBrandingType } from "@/types/misc";

const stripePromise = loadStripe(
  BrandclubStoreConfigUtils.getStripePublishableKey()
);

const Loading = () => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <div>
        <Skeleton
          variant="rectangular"
          height={50}
          sx={{ backgroundColor: "#F6F8FA" }}
        />
      </div>
      <div>
        <Skeleton
          variant="rectangular"
          height={50}
          sx={{ backgroundColor: "#F6F8FA" }}
        />
      </div>
    </div>
  );
};

export const ExperimentalCheckoutPaymentAddForm = ({
  onCancel,
}: {
  onCancel: () => void;
}) => {
  const storeType = useStoreType();
  const theme = useTheme();
  const cartProducts = useAppSelector((state) => state.checkout.products);
  const [state, send] = useCheckoutUI();
  const [isLoading, setIsLoading] = useState(false);
  const clientSecret =
    state.context.checkoutIntent?.stripePaymentIntent?.client_secret;

  if (
    !clientSecret ||
    !state.context.checkoutIntent?.checkoutIntentId ||
    isLoading
  ) {
    return <Loading />;
  }
  // this is used to display the order summary in the apple pay or google pay form
  const lineItems =
    state.context.checkoutIntent?.items?.map((item) => ({
      name: cartProducts?.[item.stacklineSku]?.title ?? "",
      amount: Math.round(item.retailPrice * 100),
    })) ?? [];
  if (
    (state.context.checkoutIntent?.estimatedOrderSummary?.shippingFees ?? 0) > 0
  ) {
    lineItems.push({
      name: "Shipping fees",
      amount: Math.round(
        (state.context.checkoutIntent?.estimatedOrderSummary?.shippingFees ??
          0) * 100
      ),
    });
  }
  if (
    (state.context.checkoutIntent?.estimatedOrderSummary?.shippingDiscount ??
      0) > 0
  ) {
    lineItems.push({
      name: "Shipping Discount",
      amount: Math.round(
        (state.context.checkoutIntent?.estimatedOrderSummary
          ?.shippingDiscount ?? 0) * 100
      ),
    });
  }
  if (
    (state.context.checkoutIntent?.estimatedOrderSummary?.giftCard ?? 0) > 0
  ) {
    lineItems.push({
      name: "Gift Card",
      amount: Math.round(
        (state.context.checkoutIntent?.estimatedOrderSummary?.giftCard ?? 0) *
          100
      ),
    });
  }
  if ((state.context.checkoutIntent?.estimatedOrderSummary?.tax ?? 0) > 0) {
    lineItems.push({
      name: "Tax",
      amount: Math.round(
        (state.context.checkoutIntent?.estimatedOrderSummary?.tax ?? 0) * 100
      ),
    });
  }

  const isDtc = storeType === StoreBrandingType.CustomDTCStore;
  const fontFamily =
    isDtc && theme.dtcFonts?.fontFamily
      ? theme.dtcFonts?.fontFamily
      : "Poppins";
  const fontFamilyLink =
    isDtc && theme.dtcFonts?.fontFamilyLink
      ? theme.dtcFonts?.fontFamilyLink
      : "https://fonts.googleapis.com/css2?family=Poppins";

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        paymentMethodCreation: "manual",
        fonts: [
          {
            cssSrc: fontFamilyLink,
          },
        ],
        appearance: {
          theme: "stripe",
          variables: {
            fontFamily: fontFamily,
            colorText: theme.palette.primary.main,
            colorTextPlaceholder: hexadecimal(theme.palette.primary.main)(50),
          },
        },
      }}
    >
      <MobileStripePaymentForm
        onCancel={onCancel}
        onShippingAddressChange={async (event) => {
          // console.log("onShippingAddressChange", event);
          event.resolve();
        }}
        onShippingRateChange={async (event) => {
          // console.log("onShippingRateChange", event);
          event.resolve();
        }}
        lineItems={lineItems}
        shippingRates={
          state.context.checkoutIntent?.shippingOptions?.map((option) => ({
            id: option.id,
            amount: Math.round(option.amount * 100),
            displayName: option.name,
            deliveryEstimate: option.deliveryEstimate,
          })) ?? [
            {
              id: "standard",
              amount: Math.round(
                (state.context.checkoutIntent?.estimatedOrderSummary
                  ?.shippingFees ?? 0) * 100
              ),
              displayName: "2-5 business days",
              deliveryEstimate: {
                minimum: {
                  unit: "business_day",
                  value: 2,
                },
                maximum: {
                  unit: "business_day",
                  value: 5,
                },
              },
            },
          ]
        }
        onSuccess={async (paymentMethod, shippingAddress, billingDetails) => {
          let shippingAddressToSubmit: CheckoutIntent["shippingAddress"] =
            undefined;
          if (shippingAddress && shippingAddress.address.line1) {
            shippingAddressToSubmit = {
              address1: shippingAddress.address.line1,
              address2: shippingAddress.address.line2 ?? "",
              city: shippingAddress.address.city,
              state: shippingAddress.address.state,
              zipCode: shippingAddress.address.postal_code,
              country: shippingAddress.address.country,
              firstName: shippingAddress.name.split(" ")[0],
              lastName: shippingAddress.name.split(" ")[1],
              phoneNumber: billingDetails?.phone ?? "",
              email: billingDetails?.email ?? "",
            };
          }
          let billingAddressToSubmit: CheckoutIntent["billingAddress"] =
            undefined;
          if (billingDetails && billingDetails.address.line1) {
            billingAddressToSubmit = {
              address1: billingDetails.address.line1,
              address2: billingDetails.address.line2 ?? "",
              city: billingDetails.address.city,
              state: billingDetails.address.state,
              zipCode: billingDetails.address.postal_code,
              country: billingDetails.address.country,
              firstName: billingDetails.name.split(" ")[0],
              lastName: billingDetails.name.split(" ")[1],
              email: billingDetails.email,
              phoneNumber: billingDetails.phone,
            };
          }
          setIsLoading(true);
          const { checkoutIntent, status, errorMessage } =
            await updatePaymentMethod({
              checkoutIntentId: state.context.checkoutIntent?.checkoutIntentId!,
              paymentMethodId: paymentMethod.id,
              shippingAddress: shippingAddressToSubmit,
              billingAddress: billingAddressToSubmit,
            });
          setIsLoading(false);

          if (status === "success") {
            send({
              type: "SET_CHECKOUT_INTENT",
              data: { checkoutIntent },
            });
          } else {
            send({
              type: "SET_CHECKOUT_ERROR",
              data: { error: errorMessage },
            });
          }

          // We refresh because there is a bug in the stripe library when you add
          // an express payment method --- you are unable to add a credit card payment method unless
          // you refresh the page.
          window.location.reload();
        }}
        onError={(error) => {
          console.error(error);
        }}
      />
    </Elements>
  );
};
